import * as React from "react";
import { RouteComponentProps } from "react-router-dom";

import { AccessReportsService } from "@app/access-reports";
import { AccessReportRunStatusChangeNotificationMessagePayload, AccessReportStatusEnum, AccessReportSummary } from "@app/access-reports/dashboard";
import { AccessReport, handleFileLinkClick } from "@app/access-reports/shared";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { ArchiveAccessReportsBulkModel } from "@app/access-reports/shared";
import { Breadcrumbs, LoadingSpinner, Page, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { NotFound } from "@app/shared/ErrorPages";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { preventDefaultMouseEvent } from "@app/shared/utility";
import { isEqual } from "lodash";
import { Roles, withUserContext, UserContextProps, RoleRestricted } from "@app/user";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { JSX } from "react";

type AccessReportOverviewProps = RouteComponentProps<{
  accessReportId: string;
}> & UserContextProps & NotificationContextProps;

interface AccessReportOverviewState {
  accessReport: AccessReport;
  loading: boolean;
  statusBanner: React.ReactNode;
}

class AccessReportOverview extends React.Component<AccessReportOverviewProps, AccessReportOverviewState> {
  private readonly accessReportsService = new AccessReportsService();

  constructor(props: AccessReportOverviewProps) {
    super(props);

    this.state = {
      accessReport: null,
      loading: true,
      statusBanner: null,
    };
  }

  public async componentDidMount() {
    await this.getAccessReport();
    this.props.subscribe(this.handleNotifications);
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
  }

  public async componentDidUpdate(prevProps: AccessReportOverviewProps, prevState: AccessReportOverviewState) {
    if (!isEqual(prevState.accessReport, this.state.accessReport)) {
      this.getStatusBanner();
    }
  }

  public render() {
    return this.state?.loading ?
      <LoadingSpinner /> :
      !this.state.accessReport ?
        <NotFound /> : (
          <Page className="bg-white" documentTitle={`${this.state.accessReport?.reportTitle || "Loading"} - Access Reports`}>
            <Breadcrumbs
              items={[
                {
                  link: "/access-reports",
                  text: "Access Reports",
                },
                {
                  text: this.state.accessReport.reportTitle,
                },
              ]}
            />
            <PageInner>
              <PageSection>
                <div className="row">
                  <div className="col-12">
                    <h3 className="mt-3 mb-3 header-l font-weight-bold">
                      {this.state.accessReport.reportTitle}

                      <hr />
                    </h3>
                  </div>
                </div>

                {this.state.statusBanner}

                {(this.state.accessReport.status === AccessReportStatusEnum.Succeeded && !this.state.loading) &&
                  this.renderResults()
                }

                {this.renderSummary()}
              </PageSection>
            </PageInner>
          </Page>
        );
  }

  private getAccessReport = async (): Promise<void> => {
    try {
      const accessReport: AccessReport = await this.accessReportsService.getAccessReport(Number(this.props.match.params.accessReportId));
      accessReport.reportTitle = !!accessReport.reportTitle ? accessReport.reportTitle.trim() : `Access Report ${accessReport.accessReportId}`

      this.setState({
        accessReport,
        loading: false,
      });
    } catch {
      this.setState({
        loading: false
      });
    }
  }

  private getStatusBanner = (): void => {
    let result: JSX.Element = null;

    switch (this.state.accessReport.status) {
      case AccessReportStatusEnum.Created:
        result = (
          <React.Fragment>
            <QESAlert classNames="mb-3" type="info" persist={true}>
              <strong>Please wait!</strong> Your project has been created and has been queued for processing.
            </QESAlert>
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Warning!</strong> You can fail your project manually if you believe it will not finish.
                <QESButton
                  qesTestId="access-reports-dashboard__overview--fail-job"
                  className="ml-3"
                  onClick={this.failProject}
                >
                  <i className="fa fa-stop-circle" /> Fail Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          </React.Fragment>
        );
        break;
      case AccessReportStatusEnum.Canceled:
      case AccessReportStatusEnum.Failed:
        if (!this.state.accessReport.dateArchived) {
          result = (
            <React.Fragment>
              <QESAlert classNames="mb-3" type="warning" persist={true}>
                <strong>Warning!</strong> Your latest project run failed.
                <QESButton
                  qesTestId="access-reports-dashboard__overview--run-again"
                  className="ml-3"
                  onClick={this.runProject}
                >
                  <i className="fa fa-rocket" /> Run Again
                </QESButton>
              </QESAlert>
              <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
                <QESAlert classNames="mb-3" type="info" persist={true}>
                  Your project can be archived.

                  <QESButton
                    qesTestId="access-reports-dashboard__overview--archive"
                    className="ml-3"
                    onClick={this.archiveProject}
                  >
                    <i className="fa fa-archive" /> Archive Project
                  </QESButton>
                </QESAlert>
              </RoleRestricted>
            </React.Fragment>
          );
        }
        else {
          result = (
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="info" persist={true}>
                Your project can be unarchived.

                <QESButton
                  qesTestId="access-reports-dashboard__overview--unarchive"
                  className="ml-3"
                  onClick={this.unarchiveProject}
                >
                  <i className="fa fa-archive" /> Unarchive Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          );
        }

        break;
      case AccessReportStatusEnum.Running:
        result = (
          <React.Fragment>
            <QESAlert classNames="mb-3" type="info" persist={true}>
              Your project is currently running.
            </QESAlert>
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Warning!</strong> You can fail your project manually if you believe it will not finish.
                <QESButton
                  qesTestId="access-reports-dashboard__overview--fail-job"
                  className="ml-3"
                  onClick={this.failProject}
                >
                  <i className="fa fa-stop-circle" /> Fail Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          </React.Fragment>
        );
        break;
      case AccessReportStatusEnum.Succeeded:
        if (!this.state.accessReport.dateArchived) {
          result = (
            <React.Fragment>
              <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
                <QESAlert classNames="mb-3" type="info" persist={true}>
                  Your project can be archived.

                  <QESButton
                    qesTestId="access-reports-dashboard__overview--archive"
                    className="ml-3"
                    onClick={this.archiveProject}
                  >
                    <i className="fa fa-archive" /> Archive Project
                  </QESButton>
                </QESAlert>
              </RoleRestricted>
            </React.Fragment>
          );
        }
        else {
          result = (
            <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>
              <QESAlert classNames="mb-3" type="info" persist={true}>
                Your project can be unarchived.

                <QESButton
                  qesTestId="access-reports-dashboard__overview--unarchive"
                  className="ml-3"
                  onClick={this.unarchiveProject}
                >
                  <i className="fa fa-archive" /> Unarchive Project
                </QESButton>
              </QESAlert>
            </RoleRestricted>
          );
        }

        break;
      case AccessReportStatusEnum.Unknown:
        break;
      default:
        console.error(`Invalid Access Report Status: ${this.state.accessReport.status}`);
    }

    const statusBanner: JSX.Element = (
      <div className="row">
        <div className="col-12">
          {result}
        </div>
      </div>
    );

    this.setState({
      statusBanner,
    })
  }

  private handleNotifications = async (message: Message): Promise<void> => {
    switch (message?.notificationType) {
      case MessageType.AccessReportsRunStatusChange:
        const projectRunStatusChangePayload: AccessReportRunStatusChangeNotificationMessagePayload = message.payload as AccessReportRunStatusChangeNotificationMessagePayload;

        if (projectRunStatusChangePayload?.accessReportId === this.state.accessReport?.accessReportId && this.state.accessReport.status !== projectRunStatusChangePayload.status) {
          this.setState((prevState) => ({
            ...prevState,
            accessReport: {
              ...prevState.accessReport,
              status: projectRunStatusChangePayload.status,
            }
          }));
        }

        break;
      default:
        break;
    }
  }

  private renderResults = (): React.ReactNode => {
    return (
      <div className="row">
        <div className="col-12">
          <legend className="mb0">Files:</legend>
        </div>

        <div className="col-12">
          <div className="mb-1">
            <TooltipWrapper containerClassName="ml-3" text="Download" triggerWidth="100%">
              <QESButton
                qesTestId="access-reports-dashboard__overview--download"
                qesType="secondary"
                onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(this.state.accessReport, true, "PDF"); }}
                className="font-weight-bold p-0"
              >
                {`${this.state.accessReport.reportTitle}.pdf`}
              </QESButton>
            </TooltipWrapper>
            <span className="ml-3">|</span>
            <TooltipWrapper containerClassName="ml-1" text="Open in new tab" triggerWidth="100%">
              <QESButton
                className="px-2 py-0"
                qesType="secondary"
                qesTestId="access-reports-dashboard__overview--open-pdf"
                onClick={() => handleFileLinkClick(this.state.accessReport, false, "PDF")}
                aria-label="Open in a new tab"
              >
                <i className="fa fa-external-link" />
              </QESButton>
            </TooltipWrapper>
          </div>
        </div>

        <div className="col-12">
          <div className="mb-1">
            <TooltipWrapper containerClassName="ml-3" text="Download" triggerWidth="100%">
              <QESButton
                qesTestId="access-reports-dashboard__overview--xlsx"
                qesType="secondary"
                onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(this.state.accessReport, true, "XLSX"); }}
                className="font-weight-bold p-0"
              >
                {`${this.state.accessReport.reportTitle}.xlsx`}
              </QESButton>
            </TooltipWrapper>
          </div>
        </div>

        {this.props.user.roles.includes(Roles.ACCESS_REPORTS_WIZARD_ADMIN) &&
          <div className="col-12">
            <div className="mb-3">
              <TooltipWrapper containerClassName="ml-3" text="Download" triggerWidth="100%">
                <QESButton
                  qesTestId="access-reports-dashboard__overview--qpx"
                  qesType="secondary"
                  onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(this.state.accessReport, true, "QPX"); }}
                  className="font-weight-bold p-0"
                >
                  {`${this.state.accessReport.reportTitle}.qpx`}
                </QESButton>
              </TooltipWrapper>
            </div>
          </div>
        }
      </div>
    );
  }

  private renderSummary = (): React.ReactNode => {
    const accessReportParameters: AccessReportParametersModel = this.state.accessReport.parameters;

    // the following properties do not get preserved in the back end (but will ALWAYS be true, as we do not submit unchecked nodes),
    // and must be turned back on to display to the ui
    for (const summaryPage of accessReportParameters.accessReportsWizardSummaryPages) {
      summaryPage.accessSummary = true;
    }

    for (const detailsPage of accessReportParameters.accessReportsWizardDetailsPages) {
      detailsPage.accessDetail = true;
    }

    for (const employeeCountPage of accessReportParameters.employeeCountPages) {
      employeeCountPage.employeeCount = true;
    }

    for (const accessOverviewPage of accessReportParameters.accessOverviewPages) {
      accessOverviewPage.accessOverview = true;
    }

    for (const accessListingPage of accessReportParameters.accessListingPages) {
      accessListingPage.accessListing = true;
    }

    for (const accessRadiusSearchPage of accessReportParameters.accessRadiusSearchPages) {
      accessRadiusSearchPage.accessRadiusSearch = true;
    }

    for (const providerCountPage of accessReportParameters.providerCountPages) {
      providerCountPage.providerCount = true;
    }

    for (const accessCriteria of accessReportParameters.product.accessCriterion) {
      accessCriteria.selected = true;
    }

    return (
      <AccessReportSummary
        accessReportParameters={accessReportParameters}
      />
    );
  }

  private runProject = async (): Promise<void> => {
    this.setState((prevState) => ({
      ...prevState,
      accessReport: {
        ...prevState.accessReport,
        status: AccessReportStatusEnum.Running,
      },
    }), async () => await this.accessReportsService.createAccessReportJob(this.state.accessReport.accessReportId));
  }

  private archiveProject = async (): Promise<void> => {
    const archive: ArchiveAccessReportsBulkModel = {
      accessReportIds: [this.state.accessReport.accessReportId],
    };

    const archived: boolean = await this.accessReportsService.archiveAccessReport(archive);

    if (archived) {
      this.setState((prevState) => ({
        ...prevState,
        accessReport: {
          ...prevState.accessReport,
          dateArchived: new Date(),
        }
      }));
    }
  }

  private unarchiveProject = async (): Promise<void> => {
    const unarchive: ArchiveAccessReportsBulkModel = {
      accessReportIds: [this.state.accessReport.accessReportId],
    };

    const unarchived: boolean = await this.accessReportsService.unarchiveAccessReport(unarchive);

    if (unarchived) {
      this.setState((prevState): AccessReportOverviewState => ({
        ...prevState,
        accessReport: {
          ...prevState.accessReport,
          dateArchived: null,
        }
      }));
    }
  }

  private failProject = async (): Promise<void> => {
    this.setState((prevState) => ({
      ...prevState,
      accessReport: {
        ...prevState.accessReport,
        status: AccessReportStatusEnum.Failed,
      },
    }), async () => await this.accessReportsService.failAccessReport(this.state.accessReport.qesJobId, this.state.accessReport.accessReportId, this.state.accessReport.userId));
  };
}

const WrappedAccessReportOverview = withNotificationContext(withUserContext(AccessReportOverview));

export default WrappedAccessReportOverview as React.ComponentClass<AccessReportOverviewProps, AccessReportOverviewState>;
